import {
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCurrentUser,
  selectHasAllGroups,
  selectIsImpersonating,
  User,
  UserGroupNames,
  PL_API_CONFIG,
  PlAPIConfig,
  PlImpersonationService,
} from 'pl-core-services';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { InternalAdminService } from '@app/common/services';
import { Account } from '@app/common/models/account';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { PLExtendAccountDeactivation } from '@app/common/store';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';
import { FeatureFlags } from '@app/common/constant/feature-flags';
import { getSecuritySettingsUrl } from '@app/common/constant/security-settings';
import { BillingService } from '@app/common/services/billing.service';
import { concatLatestFrom } from '@ngrx/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthSyncService } from '@app/auth/auth-sync.service';
import { environment } from '@environments/environment';
import { take } from 'rxjs/operators';
import { fromSubscription } from '@app/subscription/store';
import { getIsSiriusEnabled } from '@app/common/sirius-util';

@Component({
  selector: 'pl-account-dropdown',
  templateUrl: './pl-account-dropdown.component.html',
  styleUrls: ['./pl-account-dropdown.component.scss'],
})
export class PlAccountDropdownComponent implements OnInit {
  currentUser$: Observable<User> = this.store.select(selectCurrentUser);

  readonly logoutHref;

  readonly billingAdminURL$: Observable<string>;

  readonly canPurchase$: Observable<boolean>;

  readonly defaultDeactivationDate = DateTime.now()
    .startOf('day')
    .plus({ days: 7 });

  readonly deactivationDate = new UntypedFormControl(
    this.defaultDeactivationDate
  );

  isSiriusEnabled: boolean;

  minDeactivationDate: DateTime = this.defaultDeactivationDate;

  @ViewChild('deactivationModal')
  deactivationModalTemplate: TemplateRef<void>;

  isInternalAdmin: boolean;

  account: Account;

  flag$ = this.flagService.isFeatureEnabled(FeatureFlags.EXTEND_DEACTIVATION);

  showPlatformSwitcher$ = combineLatest([
    this.flagService.isFeatureEnabled(FeatureFlags.SHOW_PLATFORM_SWITCHER),
    this.store.select(
      selectHasAllGroups([
        UserGroupNames.SCHOOL_STAFF_ADMINS,
        UserGroupNames.CUSTOMER_ADMIN,
      ])
    ),
  ]).pipe(map(([flagEnabled, canSwitch]) => flagEnabled && canSwitch));

  hidePlatformSwitcherBorder$ = this.showPlatformSwitcher$.pipe(
    map((show) => show === false)
  );

  readonly presenceUrl = environment.apps.presence;

  readonly securitySettingsUrl = getSecuritySettingsUrl();

  showSecuritySettings$ = combineLatest([
    this.flagService.isFeatureEnabled(FeatureFlags.SHOW_SECURITY_SETTINGS),
    this.store.select(selectIsImpersonating),
  ]).pipe(
    map(([flagEnabled, isImpersonating]) => flagEnabled && !isImpersonating)
  );

  constructor(
    @Inject(PL_API_CONFIG) config: PlAPIConfig,
    private dialog: MatDialog,
    private store: Store,
    private flagService: FeatureFlagService,
    public internalAdminService: InternalAdminService,
    private billingService: BillingService,
    private oidcSecurityService: OidcSecurityService,
    private authSyncService: AuthSyncService,
    private impersonationService: PlImpersonationService
  ) {
    this.isSiriusEnabled = getIsSiriusEnabled();
    this.logoutHref = !this.isSiriusEnabled
      ? `${config.django_login_url}/logout/`
      : null;

    const account$ = this.internalAdminService
      .getLoadedAccount()
      .pipe(map((account) => account));

    this.billingAdminURL$ = combineLatest([
      this.flagService.isFeatureEnabled(FeatureFlags.SUBSCRIPTION_MANAGEMENT),
      this.store.select(fromSubscription.selectIsFreemium),
    ]).pipe(
      concatLatestFrom(() => account$),
      switchMap(([[isFeatureEnabled, isFreemium], account]) => {
        if (isFeatureEnabled && account.type === 'individual' && !isFreemium) {
          return this.billingService.createExternalAdminURL();
        }
        return of('');
      })
    );

    this.canPurchase$ = combineLatest([
      this.flagService.isFeatureEnabled(FeatureFlags.SUBSCRIPTION_MANAGEMENT),
      this.store.select(fromSubscription.selectCanUpgrade),
    ]).pipe(
      concatLatestFrom(() => account$),
      map(
        ([[isFeatureEnabled, isFreemium], account]) =>
          isFeatureEnabled && account.type === 'individual' && isFreemium
      )
    );
  }

  async ngOnInit() {
    this.internalAdminService.getLoadedAccount().subscribe((account) => {
      this.account = account;
      this.minDeactivationDate =
        account?.expiration_date || this.defaultDeactivationDate;
    });
  }

  openModal() {
    this.dialog.open(this.deactivationModalTemplate);
  }

  updateDeactivationDate() {
    this.store.dispatch(
      PLExtendAccountDeactivation({
        date: this.deactivationDate.value,
        accountUuid: this.account.uuid,
      })
    );
  }

  async logout() {
    if (this.isSiriusEnabled) {
      this.oidcSecurityService
        .getIdToken()
        .pipe(take(1))
        .subscribe((idToken) => {
          if (idToken) {
            const encodedIDToken = encodeURIComponent(idToken);
            const postLogoutURI = encodeURIComponent(
              `${window.location.origin}/auth/logged-out`
            );
            const endSessionUrl = `${environment.sirius_url}/oidc/end_session?id_token_hint=${encodedIDToken}&post_logout_redirect_uri=${postLogoutURI}`;

            // Clear local OIDC state (tokens, user data) without making server requests
            // This is faster and we'll handle the server-side logout via redirect below
            this.impersonationService.stop();
            this.oidcSecurityService.logoffLocal();

            // Broadcast logout to other tabs so they clear their state too
            // broadcastLogout() is async and includes a small delay to ensure message delivery
            this.authSyncService.broadcastLogout().then(() => {
              // CRITICAL: Redirect to Sirius end_session endpoint via full page navigation
              // This MUST be a redirect (not XHR/fetch) so the browser sends session cookies
              // Without cookies, Sirius can't identify which browser session to terminate
              // The end_session endpoint will revoke tokens, clear the server session,
              // and redirect back to our postLogoutRedirectUri (/auth/logged-out)
              window.location.href = endSessionUrl;
            });
          } else {
            // NOTE: This is a fallback for the case where the ID token is not available, at least have
            // `angular-auth-oidc-client` clean up its storage...
            this.impersonationService.stop();
            this.oidcSecurityService.logoffLocal();
            this.authSyncService.broadcastLogout().then(() => {
              window.location.href = '/auth/logged-out';
            });
          }
        });
    } else {
      // Handle legacy logout...
      window.location.href = this.logoutHref;
    }
  }
}
